import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { tracking } from '@dashboard/lib/tracking';
import type { StaticEntry } from './registries/types';

export type Trigger = 'cmd_k' | 'slash' | 'sidebar_click' | 'top_bar_click';

interface CommandPaletteContextValue {
  open: boolean;
  query: string;
  openPalette: (trigger?: Trigger) => void;
  closePalette: () => void;
  togglePalette: (trigger?: Trigger) => void;
  setQuery: (q: string) => void;
  pageEntries: StaticEntry[];
  setPageEntries: (entries: StaticEntry[]) => void;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

export function CommandPaletteProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [query, setQueryState] = useState('');
  const [pageEntries, setPageEntries] = useState<StaticEntry[]>([]);

  const openPalette = useCallback((trigger: Trigger = 'cmd_k') => {
    tracking.trackEvent('command_palette_opened', { trigger });
    setOpen(true);
  }, []);
  const closePalette = useCallback(() => {
    setOpen(false);
    setQueryState('');
  }, []);
  const togglePalette = useCallback((trigger: Trigger = 'cmd_k') => {
    setOpen(prev => {
      if (prev) {
        setQueryState('');
      } else {
        tracking.trackEvent('command_palette_opened', { trigger });
      }
      return !prev;
    });
  }, []);
  const setQuery = useCallback((q: string) => setQueryState(q), []);

  const value = useMemo(
    () => ({
      open,
      query,
      openPalette,
      closePalette,
      togglePalette,
      setQuery,
      pageEntries,
      setPageEntries,
    }),
    [open, query, openPalette, closePalette, togglePalette, setQuery, pageEntries],
  );

  return <CommandPaletteContext.Provider value={value}>{children}</CommandPaletteContext.Provider>;
}

export function useCommandPalette(): CommandPaletteContextValue {
  const ctx = useContext(CommandPaletteContext);
  if (!ctx) {
    throw new Error('useCommandPalette must be used inside a CommandPaletteProvider');
  }
  return ctx;
}
